import { useHistory } from 'react-router-dom'
import MenuItem from './MenuItem'
import { useSelector } from 'react-redux'
import { IStore } from '@src/store'
import GreenMenuItem from './GreenMenuItem'
import { ModalType, openModalTask } from '../../modalComponent/modalComponent'
import styles from '../index.module.scss'
import SwordsIcon from '../../icons/swords'
import ESportsIcon from '../../icons/eSports'
import SportsIcon from '../../icons/sports'
import ChipIcon from '../../icons/chip'
import CrownIcon from '../../icons/crown'
import BlogIcon from '../../icons/blog'
import HeadphonesIcon from '../../icons/headphones'
import JackToken from '@src/assets/menu/jackToken.svg'
import BecomeInvestorIcon from '../../icons/becomeInvestor'
import DealerIcon from '@src/components/icons/dealer'
import CherryIcon from '@src/components/icons/cherry'
import { BonusExpireStamp } from '@src/components/BonusExpireStamp/BonusExpireStamp'
import classNames from 'classnames'
import { RakeBackStamp } from '@src/components/RakeBackStamp'
import { SpecialBonusStamp } from '@src/components/SpecialBonusStamp/SpecialBonusStamp'
import { useTranslation } from 'react-i18next'
import { convertToK, formatNumberWithCommas, isUserLoggedIn } from '@src/utils/functions'
import ProvidersIcon from '@src/components/icons/providers'
import { useBounties } from '@src/context/BountiesContext'
import ProgressMenuItem from './ProgressMenuItem/ProgressMenuItem'
import StarIcon from '@src/components/icons/star'
import useEliteSeatProgressUpdated from '@src/hooks/useEliteSeatProgressUpdated'
import { AppTypes } from '@src/reducers/app.reducer'
import { useDispatch } from 'react-redux'
import PriceIndicator from '@src/components/PriceIndicator/PriceIndicator'
import GiftIcon from '@src/components/icons/gift'

import type { User } from '@src/stores/auth/user'
import useBJFMining from '@src/hooks/useBJFMining'
import { LanguageSelector } from './LanguageSelector/LanguageSelector'
import { useIntercom } from '@src/hooks/useIntercom'

interface Props {
  me?: User
}

const MobileMenu = ({ me }: Props) => {
  const history = useHistory()
  const collapsedMenu = useSelector((state: IStore) => state.app.collapsedMenu)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const isLoggedIn = isUserLoggedIn()
  const closeMenu = () => dispatch({ type: AppTypes.CLOSE_MENU })
  const { bountyProgress, isBountyActive, bountyWagerLeft, expiresStamp, bonus, setBountyModalVisible } = useBounties()
  const { nextBonus, wagerLeft, currentTier, progressbarProgress } = useEliteSeatProgressUpdated()
  const { miningProgressPercentage, miningWagerLeft, miningAmount } = useBJFMining()
  const { supportClick } = useIntercom()
  const progressMenuItemClickHandler = (path: string) => {
    closeMenu()
    history.push(path)
  }
  const isBonusPage = Boolean(me && me.featureFlags?.includes('bonuses'));
  return (
    <div className={`${styles.slideUpMobile} ${styles.mobileMenuContainer} px-15 flex flex-col gap-8`}>
      <div className={`${styles.scrollableMenu} px-15 flex flex-col gap-8`}>
        <PriceIndicator isCollapsed={collapsedMenu} isMobile />
        <div className="mx-5">
          <BonusExpireStamp />
          <RakeBackStamp
            rakebackClassNames={classNames(styles.rakeback, 'text-sm leading-5 font-inter font-bold')}
            rakebackPercentageClassNames={styles.rakeback_percentage}
          />
        </div>
        {isLoggedIn && (
          <>
            <ProgressMenuItem
              title={t('menu.progressItem.mineBjf', 'Mine BJF')}
              isCollapsed={collapsedMenu}
              icon={<JackToken />}
              progress={String(miningProgressPercentage)}
              wagerLeft={miningWagerLeft < 1000 ? formatNumberWithCommas(miningWagerLeft) : convertToK(miningWagerLeft)}
              expiresAt={null}
              showClaim={false}
              customTimerText={t(
                'menu.progressItem.wagerMoreToMine',
                'Wager ${{amount}} more to mine {{miningAmount}}',
                {
                  amount: miningWagerLeft,
                  miningAmount,
                }
              )}
              onClickAction={() => progressMenuItemClickHandler('/profile?tab=account&stab=BJF+Mining')}
              progressBarColor="#3CCEA2"
            />
            <ProgressMenuItem
              title={t('menu.progressItem.level', 'LEVEL {{level}}', { level: currentTier })}
              isCollapsed={collapsedMenu}
              icon={<ProvidersIcon />}
              progress={progressbarProgress}
              wagerLeft={wagerLeft < 1000 ? formatNumberWithCommas(wagerLeft) : convertToK(wagerLeft)}
              expiresAt={null}
              showClaim={false}
              customTimerText={t('menu.progressItem.nextLevelUpBonus', 'Next Level Up Bonus: ${{amount}}', {
                amount: nextBonus,
              })}
              onClickAction={() => progressMenuItemClickHandler('/profile?tab=account&stab=Elite')}
              progressBarColor="#3CCEA2"
            />
          </>
        )}
        {isBountyActive && (
          <ProgressMenuItem
            title={t('menu.progressItem.bounty', 'BOUNTY')}
            isCollapsed={collapsedMenu}
            icon={<StarIcon />}
            progress={bountyProgress}
            wagerLeft={bountyWagerLeft}
            expiresAt={expiresStamp}
            showClaim={bonus?.meta?.finished && bonus?.meta?.bounty}
            customTimerText={null}
            onClickAction={() => setBountyModalVisible(true)}
            progressBarColor={null}
          />
        )}
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<SwordsIcon />}
          label={t('menu.competitions', 'Competitions')}
          onAction={() => history.push('/competitions')}
          linkPath="/competitions"
        />
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<ChipIcon />}
          label={t('menu.casino', 'Casino')}
          onAction={() => history.push('/casino')}
          linkPath="/casino"
        />
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<DealerIcon className={'text-white'} />}
          label={t('menu.liveGames', 'Live Games')}
          onAction={() => history.push('/casino?tab=live')}
          linkPath="/casino?tab=live"
        />
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<CherryIcon className={'text-white'} />}
          label={t('menu.slots', 'Slots')}
          onAction={() => history.push('/casino?tab=slots')}
          linkPath="/casino?tab=slots"
        />
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<ProvidersIcon className={'text-white'} />}
          label={t('menu.providers', 'Providers')}
          onAction={() => history.push('/providers')}
          linkPath="/providers"
        />
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<SportsIcon />}
          label={t('menu.sports', 'Sports')}
          onAction={() => history.push('/game/btsg_sportbetting')}
        />
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<ESportsIcon />}
          label={t('menu.eSports', 'eSports')}
          onAction={() => history.push('/game/odn_oddingame')}
        />
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<CrownIcon />}
          label={t('menu.affiliate', 'Affiliate')}
          onAction={() => history.push('/profile?tab=affiliate')}
          visible={isLoggedIn}
        />
        <MenuItem
          isCollapsed={collapsedMenu}
          icon={<BlogIcon />}
          label={t('menu.blog', 'Blog')}
          onAction={() => history.push('/#blog')}
          linkPath='/#blog'
        />
          {isBonusPage && <MenuItem
          isCollapsed={collapsedMenu}
          icon={<GiftIcon />}
          label={t('menu.bonuses', 'Bonuses')}
          onAction={() => history.push('/bonuses')}
          linkPath="/bonuses"
        />}
        <LanguageSelector collapsedMenu={collapsedMenu} />
        <>
          <MenuItem
            isCollapsed={collapsedMenu}
            icon={<HeadphonesIcon />}
            label={t('menu.liveSupport', 'Live support')}
            onAction={supportClick}
          />
        </>
        <GreenMenuItem
          isCollapsed={collapsedMenu}
          icon={<BecomeInvestorIcon />}
          label={t('menu.becomeAnInvestor', 'Become an investor')}
          onAction={() => openModalTask.next({ type: ModalType.beacomeAnInvestor })}
          visible={isLoggedIn}
        />
        <GreenMenuItem
          isCollapsed={collapsedMenu}
          icon={<JackToken />}
          label={t('menu.bjfToken', 'BJF Token')}
          onAction={() => ''}
          to="/bjf-token"
          isNew
        />
        {/* <GreenMenuItem
          isCollapsed={collapsedMenu}
          icon={<BuyJackIcon />}
          label={t('menu.buyJack', 'Buy Jack')}
          onAction={() => openModalTask.next({ type: ModalType.buyJack })}
          isNew
          visible={isLoggedIn}
        /> */}
        <SpecialBonusStamp me={me} isCollapsed={collapsedMenu} />
      </div>
    </div>
  )
}

export default MobileMenu
